import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  Button,
  DialogTitle,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { FixedSizeList as List } from "react-window";
import { Close } from "@mui/icons-material";
import Decrypt from "../../../shared/utils/Decrypt";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import Swal from "sweetalert2";

const LogView = ({ openLogView, setOpenLogView, selectedCampaignId }) => {
  const serverURL = process.env.REACT_APP_URL;

  const [activityLogs, setActivityLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isCSMsSuperAdminsData, setCSMsSuperAdminsData] = useState([]);
  const [isIPsData, setIPsData] = useState([]);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [selectedIP, setSelectedIP] = useState(null);

  const [isOpen, setIsOpen] = useState(false);
  const [isIPFilterOpen, setIsIPFilterOpen] = useState(false);
  const [searchAdmin, setSearchAdmin] = React.useState("");
  const [searchIP, setSearchIP] = React.useState("");

  const [search, setSearch] = React.useState("");
  const [fromDate, setFromDate] = React.useState("");
  const [hoveredImage, setHoveredImage] = useState(null);
  const [anchorElAdmin, setAnchorElAdmin] = useState(null);

  const [toDate, setToDate] = React.useState("");

  const ROLE_MAP = {
    7: "Designer",
    0: "CSM",
    "-1": "Super Admin",
  };
  const filteredAdmins = isCSMsSuperAdminsData?.filter((admin) =>
    admin.name.toLowerCase().includes(searchAdmin.toLowerCase()),
  );
  const filterIps = isIPsData?.filter((ip) =>
    ip?.name.toLowerCase().includes(searchIP?.toLowerCase()),
  );

  const role_Map = {
    ClientUser: "(Client)",
    Vendor: "(VP)",
    InstallationPartner: "(IP)",
  };

  // ✅ FIELD LABEL MAP
  const FIELD_LABELS = {
    adminStatus: "Admin Status",
    clientStatus: "Client Status",
    adaptationImage: "Creative Image",
    adaptationText: "Creative Description",

    vendorDocumentFile: "Vendor Document File",
    JobsForInstallationPartner: "Job",
    JobApprovalStatus: "Approvals Table",
    installationPartnerId: "Installation partner",
    state: "State",
    city: "City",
    storeOwnerPhoneNumber: "Store Owner Phone Number",
    rework: "Rework",
    storeOwnerName: "Store Owner Name",
    zipCode: "Zip Code",
    address: "Address",
    storeName: "Store Name",
    productDimensionsAfterRecce: "Product Dimensions of Recce ",
    imageOfRecceSpace: "Image of Recce",
    clientSideAdaptationImage: "Client Side Creative Image",
    creativeDescription: "Client side Creative Description",
    status: "Status",
    Product: "Job",
    productType: "Product Type",
    price: "Price ",
    clientrejectionReason: "Client Rejection Reason",
    clientApprovalReason: " Client Approval Reason",
    approvedHeight: " Final Recce Height",
    approvedWidth: "Final Recce Width",
    approvalReason: "Admin Approval Reason",
    clientHoldOnReason: "Client Hold On Reason",
    cancellationRemarks: "Cancellation Remarks",
    jobCancelled: "Job Cancelled",
  };

  const formatFieldName = (field) => FIELD_LABELS[field] || field;

  // ✅ STATUS VALUE MAP
  const STATUS_VALUE_MAP = {
    adminStatus: { 0: "Pending", 1: "Approved", "-1": "Rejected" },
    clientStatus: {
      0: "Pending",
      1: "Approved",
      "-1": "Rejected",
      2: "Hold On ",
    },
    status: { 0: "Not Started", 1: "Ongoing", 2: "Completed" },
    rework: { false: "no rework", true: "to rework phase" },
  };
  const isImage = (val) => {
    if (typeof val !== "string") return false;

    return (
      val.endsWith(".png") ||
      val.endsWith(".jpg") ||
      val.endsWith(".jpeg") ||
      val.endsWith(".webp") ||
      val.endsWith(".avif")
    );
  };

  const formatValueStatus = (field, value) => {
    const mapped = STATUS_VALUE_MAP[field]?.[value];
    if (mapped !== undefined) return mapped;

    if (value === null || value === undefined || value === "")
      return "Not Available";

    // ✅ IMAGE CASE
    if (isImage(value)) {
      return (
        <img
          src={value}
          alt="preview"
          className="w-10 h-10 object-cover rounded border"
        />
      );
    }

    // ✅ DIMENSIONS
    if (field === "productDimensionsAfterRecce") {
      return `W-${value?.width}, H-${value?.height}`;
    }

    // ✅ OBJECT
    if (typeof value === "object") {
      return (
        `${value?.name || ""} ${
          value?.phoneNumber ? `(${Decrypt(value?.phoneNumber)})` : ""
        }` ||
        value?.title ||
        "Not Available"
      );
    }

    return String(value);
  };
  // ✅ FETCH DATA
  const fetchCampaignLogs = async () => {
    try {
      setLoading(true);
      const res = await fetch(
        `${serverURL}/activityLogs/getAllActivityLogs?campaignId=${selectedCampaignId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );

      const json = await res.json();

      // ✅ IMPORTANT FIX (avoid forEach error)
      setActivityLogs(Array.isArray(json?.data) ? json?.data : []);
      setCSMsSuperAdminsData(json?.adminsData);
      setIPsData(json?.installationPartnerData);
    } catch (error) {
      console.error("API error:", error);
      setActivityLogs([]);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (selectedCampaignId) fetchCampaignLogs();
  }, [selectedCampaignId]);

  const dropdownRef = React.useRef();
  const dropdownRefIP = React.useRef();

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef?.current &&
        !dropdownRef?.current?.contains(event.target)
      ) {
        setIsOpen(false);
      }
      if (
        dropdownRefIP?.current &&
        !dropdownRefIP?.current?.contains(event.target)
      ) {
        setIsIPFilterOpen(false);
      }
    };

    document?.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ✅ FILTER
  const filteredLogs = React.useMemo(() => {
    if (!Array?.isArray(activityLogs)) return [];

    return activityLogs?.filter((log) => {
      const nameMatch = selectedAdmin
        ? String(log.adminId?._id) === String(selectedAdmin._id)
        : true;
      const ipNameMatch = selectedIP
        ? String(log.otherUserId?._id) == String(selectedIP._id)
        : true;

      const logDate = new Date(log.createdAt);

      const from = fromDate ? new Date(fromDate) : null;
      const to = toDate ? new Date(toDate) : null;

      const dateMatch =
        (!from || logDate >= from) &&
        (!to || logDate <= new Date(to?.setHours(23, 59, 59)));

      const fieldMatch = search
        ? (log.fieldUpdated + " " + formatFieldName(log.fieldUpdated))
            .toLowerCase()
            .includes(search.toLowerCase())
        : true;

      return nameMatch && dateMatch && fieldMatch && ipNameMatch;
    });
  }, [activityLogs, search, fromDate, toDate, selectedAdmin, selectedIP]);

  // ✅ SORT
  const sortedLogs = React.useMemo(() => {
    return [...filteredLogs].sort(
      (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
    );
  }, [filteredLogs]);

  // ✅ GROUP BY DATE
  const groupByDate = (logs) => {
    if (!Array?.isArray(logs)) return [];

    const groups = [];

    logs.forEach((log) => {
      const date = new Date(log?.createdAt);
      const today = new Date();

      let label = date?.toLocaleDateString("en-GB");

      if (date?.toDateString() === today?.toDateString()) {
        label = "Today";
      } else {
        const yesterday = new Date();
        yesterday?.setDate(today.getDate() - 1);

        if (date?.toDateString() === yesterday?.toDateString()) {
          label = "Yesterday";
        }
      }

      let group = groups?.find((g) => g.label === label);

      if (!group) {
        group = { label, logs: [] };
        groups.push(group);
      }

      group?.logs.push(log);
    });

    return groups;
  };

  const groupedLogs = React.useMemo(
    () => groupByDate(sortedLogs),
    [sortedLogs],
  );

  // ✅ FLATTEN
  const flatList = React.useMemo(() => {
    if (!Array.isArray(groupedLogs)) return [];

    const items = [];

    groupedLogs?.forEach((group) => {
      items?.push({ type: "header", label: group.label });

      (group?.logs || [])?.forEach((log) => {
        items.push({ type: "item", data: log });
      });
    });

    return items;
  }, [groupedLogs]);

  // ✅ ELEMENT DISPLAY
  const TABLE_UI_CONFIG = {
    JobsForInstallationPartner: (d) => ({
      title: d?.storeName || "Not Available",
      subtitle: `${d?.address || ""}, ${d?.city || ""}, ${d?.state || ""}`,
    }),
    JobApprovalStatus: (d) => ({
      title: d?.jobDetails?.storeName || "Not Available",
      subtitle: `${d?.jobDetails?.address || ""}, ${d?.jobDetails?.city || ""}, ${d?.jobDetails?.state || ""}`,
    }),
    Product: (d) => ({
      title: d?.jobDetails[0]?.storeName || "Not Available",
      subtitle: `${d?.jobDetails[0]?.address || ""}, ${d?.jobDetails[0]?.city || ""}, ${d?.jobDetails[0]?.state || ""}`,
    }),
  };

  const getElementDisplay = (tableName, element) => {
    if (!element) return { title: "Not Available", subtitle: "" };

    return (
      TABLE_UI_CONFIG[tableName]?.(element) || {
        title: element?._id || "Unknown",
        subtitle: "",
      }
    );
  };

  let ignoreUpdatedValues = ["clientId", "uploadFromPortal", "fromw"];

  // ✅ ROW RENDER
  const Row = ({ index, style }) => {
    const row = flatList[index];
    if (!row) return null;

    // ================= HEADER =================
    if (row.type === "header") {
      const isToday = row?.label === "Today";
      const isYesterday = row?.label === "Yesterday";

      return (
        <div
          style={style}
          className="sticky top-0 z-20 bg-white px-4 py-2 flex items-center gap-3  border-b shadow-sm"
        >
          <span
            className={`px-3 py-1 text-xs rounded-full font-semibold tracking-wide
          ${
            isToday
              ? "bg-blue-100 text-blue-600"
              : isYesterday
                ? "bg-gray-200 text-gray-700"
                : "bg-gray-100 text-gray-600"
          }`}
          >
            {row?.label}
          </span>

          <div className="flex-1 h-[1px] bg-gray-200"></div>
        </div>
      );
    }

    // ================= DATA ROW =================
    const log = row?.data;

    const { title, subtitle } = getElementDisplay(
      log?.tableName,
      log?.elementId,
    );

    const time = new Date(log.createdAt).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });

    const profilePhoto =
      log?.adminId?.profilePhoto ||
      log?.clientId?.organizationId?.brandLogo ||
      log?.otherUserId?.profilePhoto;
    const userName =
      log?.adminId?.name ||
      log?.clientId?.name ||
      log?.otherUserId?.name ||
      "User";
    const prev = log.previousValue;
    const next = log.newValue;

    let isImageChange = isImage(prev) || isImage(next);

    return (
      <div
        style={style}
        className="group flex items-start gap-3 px-4 py-2 relative rounded-lg transition-all "
      >
        {/* 🔵 PROFILE */}
        <div className="flex-shrink-0 mt-2">
          {profilePhoto ? (
            <img
              src={profilePhoto}
              alt="profile"
              className="w-20 h-20 mt-4 rounded-full object-cover border shadow-sm"
            />
          ) : (
            <div className="w-20 h-20 mt-4 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-[32px] font-semibold text-white shadow">
              {userName[0]}
            </div>
          )}
        </div>

        {/* 🔹 TIMELINE */}
        <div className="relative flex-1 pl-6">
          {/* Vertical line */}

          {/* Dot */}
          <div className="absolute left-[7px] top-14 w-3 h-3 bg-blue-500 border-2 border-white rounded-full shadow"></div>
          {/* 🔹 CARD */}
          <div className="bg-white border border-gray-100 p-3 rounded-lg shadow-sm flex justify-between items-start gap-4 hover:bg-blue-50 transition">
            {/* 🔹 LEFT SIDE (MAIN CONTENT) */}
            <div className="flex-1 min-w-0">
              {/* Header */}
              <p className="text-sm text-gray-700 truncate">
                <span className="font-semibold">
                  {`${userName} ${role_Map[log?.otherUserSchema] ?? ""}`}
                  {`${log?.clientId?.name ? " (client)" : ""}`}{" "}
                </span>{" "}
                updated{" "}
                <span className="font-semibold text-blue-600">
                  {formatFieldName(log.fieldUpdated)}
                </span>{" "}
                in{" "}
                <span className="font-semibold text-gray-900">
                  {formatFieldName(log.tableName)}
                </span>
              </p>
              {/* Title */}
              <p className="font-medium text-sm text-gray-900 truncate">
                {title}
              </p>
              {/* Subtitle */}
              {subtitle && (
                <p className="text-xs text-gray-500 truncate">{subtitle}</p>
              )}
              {/* Time */}
              <p className="text-[11px] text-gray-400 mt-1 flex items-center gap-1">
                <AccessTimeIcon sx={{ fontSize: 14 }} />
                {time}
              </p>{" "}
            </div>

            {/* 🔹 RIGHT SIDE (VALUE CHANGE COLUMN) */}

            {
              <div
                className={`flex ${
                  isImageChange
                    ? "flex-row items-center gap-2"
                    : "flex-col items-end"
                } text-xs min-w-[90px]`}
              >
                {/* ✅ IMAGE UI */}
                {isImageChange ? (
                  <>
                    {/* Prev Image */}
                    <img
                      src={typeof prev === "object" ? prev?.url : prev}
                      alt="prev"
                      className="w-20 h-20 object-cover rounded border"
                      onMouseEnter={(e) => {
                        setHoveredImage(
                          typeof prev === "object" ? prev?.url : prev,
                        );
                        setAnchorElAdmin(e.currentTarget);
                      }}
                      onMouseLeave={() => {
                        setHoveredImage(null);
                        setAnchorElAdmin(null);
                      }}
                    />

                    <span className="text-gray-400">→</span>

                    {/* New Image */}
                    <img
                      src={typeof next === "object" ? next?.url : next}
                      alt="new"
                      className="w-20 h-20 object-cover rounded border"
                      onMouseEnter={(e) => {
                        setHoveredImage(
                          typeof next === "object" ? next?.url : next,
                        );
                        setAnchorElAdmin(e.currentTarget);
                      }}
                      onMouseLeave={() => {
                        setHoveredImage(null);
                        setAnchorElAdmin(null);
                      }}
                    />
                  </>
                ) : (
                  <>
                    {/* TEXT UI (old one) */}
                    <span className="text-[10px] text-gray-400">Prev</span>
                    <span className="line-through text-red-400 font-medium">
                      {formatValueStatus(log.fieldUpdated, prev)}
                    </span>

                    <span className="text-gray-400 my-[2px]">↓</span>

                    <span className="text-[10px] text-gray-400">Now</span>
                    <span className="text-green-500 font-semibold">
                      {formatValueStatus(log.fieldUpdated, next)}
                    </span>
                  </>
                )}
              </div>
            }
          </div>
        </div>
      </div>
    );
  };

  return (
    <Dialog
      open={openLogView}
      onClose={(e, reason) => {
        if (reason !== "backdropClick") setOpenLogView(false);
      }}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          height: "90vh", // ✅ increase height
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle className="flex justify-between items-center">
        Activity Logs
        <IconButton
          onClick={() => setOpenLogView(false)}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "gray",
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <div className="bg-white p-4 rounded-xl shadow-sm border mb-4">
          <div className="flex flex-wrap gap-4 items-end">
            {/* 🔍 Search */}
            <div ref={dropdownRef} className="flex flex-col relative w-[260px]">
              {" "}
              <label className="text-xs font-semibold text-gray-500 mb-1">
                Select Admin
              </label>
              {/* Selected Box */}
              <div
                onClick={() => setIsOpen(true)}
                className="border rounded-lg px-3 py-2 text-sm bg-white cursor-pointer flex justify-between items-center hover:border-blue-400"
              >
                <span className="flex items-center gap-2">
                  {selectedAdmin ? (
                    <>
                      <span className="text-gray-800 font-medium">
                        {selectedAdmin.name}
                      </span>

                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedAdmin(null);
                        }}
                        className="text-red-400 hover:text-red-600 cursor-pointer"
                      >
                        ✕
                      </span>
                    </>
                  ) : (
                    <span className="text-gray-400">Select Admin</span>
                  )}
                </span>

                <span className="text-gray-400">▼</span>
              </div>
              {/* Dropdown */}
              {isOpen && (
                <div className="absolute top-full mt-1 w-full bg-white border rounded-lg shadow-lg z-[999] max-h-60 overflow-auto">
                  {" "}
                  {/* Search inside dropdown */}
                  <input
                    type="text"
                    placeholder="Search..."
                    className="w-full px-3 py-2 border-b text-sm outline-none"
                    value={searchAdmin}
                    onChange={(e) => setSearchAdmin(e.target.value)}
                  />
                  {/* List */}
                  {filteredAdmins?.length > 0 ? (
                    filteredAdmins?.map((admin) => (
                      <div
                        key={admin._id}
                        onClick={(e) => {
                          e.stopPropagation();

                          setSelectedAdmin(admin);
                          setIsOpen(false);
                        }}
                        className="px-3 py-2 hover:bg-blue-50 cursor-pointer border-b last:border-none"
                      >
                        <div className="flex justify-between items-center">
                          {/* Left */}
                          <div>
                            <p className="text-sm font-semibold">
                              {admin.name}
                            </p>
                            <p className="text-xs text-gray-500">
                              📞 {admin.phoneNumber}
                            </p>
                          </div>

                          {/* Role Badge */}
                          <span
                            className={`text-xs px-2 py-0.5 rounded-full font-medium
                  ${
                    admin.role === 7
                      ? "bg-purple-100 text-purple-600"
                      : admin.role === 0
                        ? "bg-blue-100 text-blue-600"
                        : "bg-gray-200 text-gray-600"
                  }`}
                          >
                            {ROLE_MAP[admin.role] || "Unknown"}
                          </span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-gray-400 p-3">
                      No results found
                    </p>
                  )}
                </div>
              )}
            </div>
            <div
              ref={dropdownRefIP}
              className="flex flex-col relative w-[260px]"
            >
              {" "}
              <label className="text-xs font-semibold text-gray-500 mb-1">
                Select IP
              </label>
              {/* Selected Box */}
              <div
                onClick={() => setIsIPFilterOpen(true)}
                className="border rounded-lg px-3 py-2 text-sm bg-white cursor-pointer flex justify-between items-center hover:border-blue-400"
              >
                <span className="flex items-center gap-2">
                  {selectedIP ? (
                    <>
                      <span className="text-gray-800 font-medium">
                        {selectedIP.name}
                      </span>

                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedIP(null);
                        }}
                        className="text-red-400 hover:text-red-600 cursor-pointer"
                      >
                        ✕
                      </span>
                    </>
                  ) : (
                    <span className="text-gray-400">Select IP</span>
                  )}
                </span>

                <span className="text-gray-400">▼</span>
              </div>
              {/* Dropdown */}
              {isIPFilterOpen && (
                <div className="absolute top-full mt-1 w-full bg-white border z-[999] rounded-lg shadow-lg  max-h-60 overflow-auto">
                  {" "}
                  {/* Search inside dropdown */}
                  <input
                    type="text"
                    placeholder="Search..."
                    className="w-full px-3 py-2 border-b text-sm outline-none"
                    value={searchIP}
                    onChange={(e) => setSearchIP(e.target.value)}
                  />
                  {/* List */}
                  {filterIps?.length > 0 ? (
                    filterIps?.map((ip) => (
                      <div
                        key={ip._id}
                        onClick={() => {
                          setSelectedIP(ip);
                          setIsIPFilterOpen(false);
                        }}
                        className="px-3 py-2 hover:bg-blue-50 cursor-pointer border-b last:border-none"
                      >
                        <div className="flex justify-between items-center">
                          {/* Left */}
                          <div>
                            <p className="text-sm font-semibold">{ip.name}</p>
                            <p className="text-xs text-gray-500">
                              📞 {Decrypt(ip.phoneNumber)}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-gray-400 p-3">
                      No results found
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="flex flex-col">
              <label className="text-xs font-semibold text-gray-500 mb-1">
                From Date
              </label>
              <input
                type="date"
                className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition"
                value={fromDate}
                max={toDate || undefined} // ✅ restrict future beyond toDate
                onChange={(e) => {
                  const newFromDate = e.target.value;

                  // reset toDate if invalid
                  if (toDate && toDate < newFromDate) {
                    setToDate("");
                  }

                  setFromDate(newFromDate);
                }}
              />
            </div>

            {/* 📅 To Date */}
            <div className="flex flex-col">
              <label className="text-xs font-semibold text-gray-500 mb-1">
                To Date
              </label>
              <input
                type="date"
                className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition"
                value={toDate}
                min={fromDate || undefined} // ✅ correct
                onChange={(e) => {
                  const selectedToDate = e.target.value;

                  if (fromDate && selectedToDate < fromDate) {
                    Swal.fire("To Date cannot be earlier than From Date");
                    return;
                  }

                  setToDate(selectedToDate);
                }}
              />
            </div>
            <div className="flex flex-col">
              <label className="text-xs font-semibold text-gray-500 mb-1">
                Search Updated Field
              </label>
              <input
                type="text"
                placeholder="Type field (e.g.Admin status, city)"
                className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <button
              onClick={() => {
                setSearch("");
                setFromDate("");
                setToDate("");
                setSelectedAdmin(null);
              }}
              className="h-[38px] px-4 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition"
            >
              Clear
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center w-full h-[400px] items-center">
            <CircularProgress />
          </div>
        ) : flatList.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-[300px] text-gray-500">
            <div className="text-4xl mb-2">📭</div>

            <p className="text-sm font-medium">
              No logs generated for this campaign
            </p>

            <p className="text-xs text-gray-400 mt-1">
              Activity will appear here once actions are performed
            </p>
          </div>
        ) : (
          <div className="relative overflow-hidden">
            {hoveredImage && anchorElAdmin && (
              <div
                style={{
                  position: "fixed",
                  top: anchorElAdmin.getBoundingClientRect().top + 10,
                  left: anchorElAdmin.getBoundingClientRect().left + 40,
                  zIndex: 9999,
                  background: "#fff",
                  padding: "8px",
                  borderRadius: "8px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                }}
              >
                <img
                  src={hoveredImage}
                  alt="preview"
                  style={{
                    width: "200px",
                    height: "auto",
                    borderRadius: "6px",
                  }}
                />
              </div>
            )}
            <div className="absolute left-[120px] top-0 bottom-2 w-[2px] bg-gray-200"></div>
            <List
              height={460}
              itemCount={flatList.length}
              itemSize={125} // or fixed
              width="100%"
              style={{ paddingBottom: "10px" }} // ✅ FIX
            >
              {Row}
            </List>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default LogView;
